/**
 * L2 — the full task list, filterable by when it has to be done.
 *
 * The board shows the first eight; this shows all of them, grouped by urgency
 * so the shape of the day is readable before a single row is. It renders the
 * *same* `ActionRow` as the board rather than a second design: two renderings of
 * one list is how two lists start disagreeing.
 */

import {
  Alert,
  type BreadcrumbItem,
  Breadcrumbs,
  EmptyState,
  PageHeader,
  Skeleton,
} from "@norican/react";
import type { ReactNode } from "react";
import { Link } from "react-router";

import { FilterRow } from "@/components/board";
import { useBackupActions } from "@/lib/api";
import { formatCount } from "@/lib/charts/config";
import { useDrillParams } from "@/lib/drill";

import { ActionRow, ORDER, URGENCY } from "./actions-section";
import { PanelCard } from "./panel-card";
import { URGENCY_PARAM } from "./params";

const BREADCRUMBS: BreadcrumbItem[] = [
  {
    label: "Backup",
    render: ({ className, children }: { className: string; children: ReactNode }) => (
      <Link to="/backup" className={className}>
        {children}
      </Link>
    ),
  },
  { label: "What needs you", current: true },
];

export function BackupActionsPage() {
  const actions = useBackupActions();
  const drill = useDrillParams(URGENCY_PARAM);
  const chosen = drill.getList(URGENCY_PARAM);

  const data = actions.data;
  const all = data?.actions ?? [];
  const rows = all.filter((row) => chosen.length === 0 || chosen.includes(row.urgency));

  return (
    <main className="mx-auto flex max-w-7xl flex-col gap-6 px-6 py-8">
      <Breadcrumbs items={BREADCRUMBS} />
      <PageHeader
        title="What needs you"
        subtitle="Every open finding on the backup board, ranked by when it has to be dealt with."
      />

      {actions.error && (
        <Alert tone="danger" title="Couldn't load the task list">
          {actions.error instanceof Error ? actions.error.message : "The NOVA API did not respond."}
        </Alert>
      )}

      {data?.stale && (
        <Alert tone="warning" title="A source has gone quiet">
          These counts are a floor rather than a total — nothing new can arrive from a source that
          is not reporting.
        </Alert>
      )}

      {actions.isPending && !data ? (
        <Skeleton height="24rem" />
      ) : (
        data && (
          <>
            <FilterRow
              showRange={false}
              dimensions={[
                {
                  param: URGENCY_PARAM,
                  label: "When",
                  noun: "bucket",
                  options: ORDER.map((urgency) => ({
                    value: urgency,
                    label: URGENCY[urgency].label,
                  })),
                },
              ]}
            />

            {/* Grouped rather than one flat list: the shape of the day is
                readable before any single row is, and an empty "Fix today" is
                information in its own right. */}
            {ORDER.filter((urgency) => chosen.length === 0 || chosen.includes(urgency)).map(
              (urgency) => {
                const group = rows.filter((row) => row.urgency === urgency);
                return (
                  <PanelCard
                    key={urgency}
                    title={URGENCY[urgency].label}
                    caption={`${formatCount(group.length)} · ${URGENCY[urgency].meaning}`}
                  >
                    {group.length === 0 ? (
                      <EmptyState
                        size="sm"
                        title={
                          urgency === "now"
                            ? "Nothing broke today"
                            : `Nothing in ${URGENCY[urgency].label.toLowerCase()}`
                        }
                        description={
                          urgency === "now"
                            ? "Every job that ran succeeded and every source is reporting."
                            : "This bucket is empty."
                        }
                      />
                    ) : (
                      <ul className="flex flex-col gap-2">
                        {group.map((action) => (
                          <ActionRow key={action.id} action={action} />
                        ))}
                      </ul>
                    )}
                  </PanelCard>
                );
              },
            )}
          </>
        )
      )}
    </main>
  );
}
